import { Anchor, Callout, CalloutText, Paragraph } from "@twilio-paste/core";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../../Button";
import { LOGO_ALT, TWILIO_SENDGRID_LOGO } from "../../constants";
import ErrorAlert from "../../ErrorAlert";
import { useFormState } from "../../hooks/useFormState";
import { peekMovedParam } from "../../movedQueryParams";
import { isAllowListedUrl, isRelativeUrl, sanitizeUrl } from "../../navigationHelpers";
import { TextInput } from "../../TextInput";
import { fetchVerifyEmail } from "../../unified-auth/fetch";
import { MARKETPLACE_CHANGELOG_URL, RESELLER_FLOW_FLAG } from "./constants";
import {
  getErrorMessageByCode,
  teammateInviteInternalErrorMessage,
  teammateInviteInvalidTokenErrorMessage,
} from "./errorMessages";

// Error id that means the invite token is invalid/expired (or its validation count is exhausted).
// Retrying a dead token is futile, so this is treated as a terminal state on the page. (CLRF-2268)
const ERR_TEAMMATE_INVITE_INVALID_TOKEN = "ERR_TEAMMATE_INVITE_INVALID_TOKEN";

export default function UnifiedLoginIdentifier() {
  const [searchParams] = useSearchParams();

  // Read at render (not module load) so tests can stub it. Unset or anything but "true" hides the callout.
  // VITE_ENABLE_MARKETPLACE_CHANGELOG_CALLOUT env var was introduced 11/2026 via CLRF-2357. If you're
  // reading this more than a year later, please feel free to rip this env var and callout out entirely
  // as customers will be used to it by now.
  const showChangelogCallout = import.meta.env.VITE_ENABLE_MARKETPLACE_CHANGELOG_CALLOUT === "true";

  // index.html moves the invite `token` out of the URL into sessionStorage before analytics load, so
  // it may only be present there. `peekMovedParam` is a plain read (no side effect), safe to call on
  // every render. We PEEK rather than consume because the token must survive the full-page navigation
  // to /unified_login/start, which is the single consumer that removes it. Do NOT switch this to
  // consume, or the hand-off breaks.
  const sessionToken = peekMovedParam("token");
  const token = searchParams.get("token") || sessionToken;

  // An incoming ?error= arrives when a failed post-Auth0 teammate-accept reroutes back here
  // (tryRedirectingUser, CLRF-2268 Bug #1/#2). We render it, but an invalid/expired token is a
  // terminal state — retrying a dead token is futile — whereas other (transient) errors keep the
  // form so the user can try again with the token that was written back to sessionStorage.
  const errorParam = searchParams.get("error");
  const isInvalidTokenError = errorParam === ERR_TEAMMATE_INVITE_INVALID_TOKEN;

  const [email, , changeEmail] = useFormState("");
  const [errorBanner, setErrorBanner] = useState<string | JSX.Element | undefined>(() => {
    if (!errorParam || isInvalidTokenError) {
      return "";
    }
    // Fall back to the generic message (and log) for an unrecognized error id, so an unexpected
    // reroute never shows the form with no banner and no signal. (CLRF-2268 S3)
    const message = getErrorMessageByCode(errorParam);
    if (!message) {
      console.error(`Unrecognized error id in ?error=: ${errorParam}`);
      return teammateInviteInternalErrorMessage;
    }
    return message;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Terminal state (no form): there is no token to submit, or the token is invalid/expired — nothing
  // is actionable in-page, so show a single message directing the user to restart from the Marketplace.
  if (!token || isInvalidTokenError) {
    let terminalMessage: string | JSX.Element = teammateInviteInvalidTokenErrorMessage;
    if (!isInvalidTokenError && errorParam) {
      const mapped = getErrorMessageByCode(errorParam);
      if (mapped) {
        terminalMessage = mapped;
      } else {
        // Parity with the banner path: log an unrecognized id for observability. Keep the "start
        // over" message — there's no token to submit here regardless of the code. (CLRF-2268 review S3)
        console.error(`Unrecognized error id in ?error=: ${errorParam}`);
      }
    }
    return (
      <main className="login">
        <ErrorAlert input={terminalMessage} />
        <div className="login-container">
          <div data-role="login-modals" className="login-modals">
            <div className="login-modal">
              <div className="login-modal-table">
                <div className="login-modal-table-row">
                  <div className="login-modal-body">
                    <div className="login-logo">
                      <img alt={LOGO_ALT} src={TWILIO_SENDGRID_LOGO} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const formSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorBanner("");
    setIsLoading(true);

    try {
      const emailTrimmed = email.trim();
      const result = await fetchVerifyEmail(token, emailTrimmed);
      if (result.verified) {
        // Defensive check: Chaos should always return a defined target URL on successful
        // verification, but we check client-side just in case to avoid the sanitizeUrl
        // function returning "/" and doing a silent redirect to the login app root.
        if (!result.target) {
          setErrorBanner(teammateInviteInternalErrorMessage);
          return;
        }

        // The Chaos backend constrains the target URL to be either:
        // https://login.sendgrid.com/unified_login/start or
        // https://login.sendgrid.com/unified_login/start?screen_hint=signup
        // Not listed in the two example URLs above are the three additional encoded query parameters
        // that will always be included in the URL returned by Chaos in the target field of the
        // verify-email payload: email=[email address], token=[token], and prompt=login

        // As a safeguard, we enforce the target URL client-side is an
        // absolute URL and not a relative URL.
        const sanitized = sanitizeUrl(result.target);
        if (sanitized === "/" || isRelativeUrl(sanitized)) {
          // enforce absolute URL in result.target since Chaos should only return absolute URLs.
          setErrorBanner(teammateInviteInternalErrorMessage);
          return;
        }

        // Navigate to the RAW target, not the decoded `sanitized`. sanitizeUrl runs
        // decodeURIComponent on the whole URL, which corrupts percent-encoded reserved characters in
        // the query (e.g. a plus-addressed email arrives as `%2B`; decoding turns it into `+`, which
        // the next page reads as a space). We still validate: `sanitized` gates the scheme + control
        // characters above, and we require BOTH the decoded copy AND the raw target to resolve to an
        // allow-listed origin, so the string we navigate to is one we validated. (CLRF-2268 Bug #3.)
        if (isAllowListedUrl(sanitized) && isAllowListedUrl(result.target)) {
          // Mark this as the reseller flow so /unified_login/start threads `fromReseller` into
          // appState; a failed teammate-accept then reroutes back here instead of /login/identifier.
          sessionStorage.setItem(RESELLER_FLOW_FLAG, "true");
          window.location.href = result.target;
          return;
        }
        setErrorBanner(teammateInviteInternalErrorMessage);
      } else {
        const errorMessage =
          getErrorMessageByCode(result.error_id, emailTrimmed) ||
          teammateInviteInternalErrorMessage;
        setErrorBanner(errorMessage);
      }
    } catch {
      setErrorBanner(teammateInviteInternalErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="login">
      {errorBanner && <ErrorAlert input={errorBanner} />}

      <div className="login-container">
        <div data-role="login-modals" className="login-modals">
          <div className="login-modal">
            <div className="login-modal-table">
              <div className="login-modal-table-row">
                <div className="login-modal-body">
                  <div className="login-logo">
                    <img alt={LOGO_ALT} src={TWILIO_SENDGRID_LOGO} />
                  </div>
                  <form data-role="login-form" className="login-form" onSubmit={formSubmit}>
                    <div className="row">
                      <div className="col-8 col-start-3">
                        <Paragraph marginBottom="space0">
                          Enter your email address to log in to your marketplace account.
                        </Paragraph>
                        <TextInput
                          label="Email address"
                          id="email"
                          value={email}
                          type="email"
                          autoFocus={true}
                          autoComplete="username"
                          onChange={changeEmail}
                          containerRole={"emailContainer"}
                          ariaDescribedBy="email_callout_text"
                        />
                        <Callout variant="neutral" marginY="space40">
                          <CalloutText id="email_callout_text">
                            Use your twilio.com login email address. If you do not have one, a new
                            login will be created for you.
                          </CalloutText>
                        </Callout>
                        {showChangelogCallout && (
                          <Callout variant="new" marginY="space40">
                            <CalloutText>
                              Not sure why you&apos;re seeing this page? See this{" "}
                              <Anchor href={MARKETPLACE_CHANGELOG_URL} showExternal>
                                changelog
                              </Anchor>{" "}
                              for recent changes to our marketplace login process.
                            </CalloutText>
                          </Callout>
                        )}
                      </div>
                    </div>
                    <div className="login-btn">
                      <Button
                        dataRole="continue-btn"
                        className="login-sso-btn"
                        isLoading={isLoading}
                        isDisabled={isLoading || email.trim() === ""}>
                        Continue
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
